/**
 * useCoarsePointer — true when the primary pointer is a finger
 * (`(pointer: coarse)`: an iPad, a touch kiosk), false on a mouse / trackpad
 * device and wherever the answer is unknown (SSR, the test DOM). The
 * cafeteria count boxes read it to trade the system keyboard for their own
 * keypad (`CountInput`).
 *
 * Read SYNCHRONOUSLY in the state initializer, not first in an effect: a box
 * that is focused on mount (the inventory dialog opening on a row) takes the
 * focus before an effect's re-render lands, and iOS reads `inputmode` at that
 * focus — an effect-first read would raise the system keyboard once before
 * the box asks for none. Stays live through the query's `change` event (a
 * trackpad attached, DevTools emulation toggled).
 */

import { useEffect, useState } from 'react'

import { COARSE_POINTER_QUERY } from '@/lib/cafeteria/tablet-mode'

function readCoarsePointer(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia(COARSE_POINTER_QUERY).matches
  )
}

export function useCoarsePointer(): boolean {
  const [coarse, setCoarse] = useState(readCoarsePointer)
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return
    }
    const media = window.matchMedia(COARSE_POINTER_QUERY)
    const update = () => setCoarse(media.matches)
    // Anything that moved between the initializer and this subscription.
    update()
    media.addEventListener('change', update)
    return () => media.removeEventListener('change', update)
  }, [])
  return coarse
}
